import React, { useEffect, useState } from 'react';
import { PART_TYPES, remainingCents, partProblem, loanPaymentParts, findInterestExpense } from './splitParts';
import { formatApiError } from '../../utils/errorMessage';

const blank = (type = 'income') => ({ transaction_type: type, category_id: '', amount: '', memo: '', counter_account_id: '' });

const STATUS_TEXT = {
  linked: 'linked to a matching row there',
  created: 'recorded there',
  pending: 'waiting for that bank account\'s next sync; save again to link it'
};

// Split one transaction into parts that add up to its amount. A Transfer part
// can name the other account; its other side is linked or created there.
export default function SplitModal({ transaction, categories, accounts, api, onClose, onSaved }) {
  const [parts, setParts] = useState([blank(transaction.transaction_type), blank('transfer')]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loan, setLoan] = useState(null);

  const others = (accounts || []).filter((a) => Number(a.id) !== Number(transaction.account_id));
  const interestCategory = findInterestExpense(categories);

  useEffect(() => {
    let live = true;
    api.getSplits(transaction.id).then((res) => {
      if (live && res.data?.length) {
        setParts(res.data.map((p) => ({
          ...p,
          category_id: p.category_id || '',
          memo: p.memo || '',
          counter_account_id: p.counter_account_id ? String(p.counter_account_id) : ''
        })));
      }
    }).catch(() => {});
    return () => { live = false; };
  }, [api, transaction.id]);

  const left = remainingCents(transaction.amount, parts);
  const problem = parts.map(partProblem).find(Boolean) || (left !== 0 ? 'The parts must add up to the transaction amount' : null);
  const update = (i, key, value) => setParts(parts.map((p, j) => (j === i ? { ...p, [key]: value } : p)));

  const loanParts = loan && loanPaymentParts(transaction.amount, {
    loanAccountId: loan.account, principal: loan.principal, interest: loan.interest, interestCategoryId: interestCategory?.id
  });
  const applyLoan = () => {
    if (!loanParts) return;
    setParts(loanParts);
    setLoan(null);
  };

  const run = async (call) => {
    setSaving(true);
    setError(null);
    try {
      await call();
      onSaved();
    } catch (e) {
      setError(formatApiError(e, 'Could not save the split'));
    } finally {
      setSaving(false);
    }
  };
  const save = () => run(() => api.saveSplits(transaction.id, parts.map((p) => ({
    transaction_type: p.transaction_type,
    category_id: p.category_id || null,
    amount: p.amount,
    memo: p.memo,
    counter_account_id: p.transaction_type === 'transfer' && p.counter_account_id ? p.counter_account_id : null
  }))));
  const remove = () => run(() => api.removeSplits(transaction.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold mb-1">Split transaction</h3>
        <p className="text-sm text-gray-600 mb-4">
          {transaction.description} {'\u00b7'} {Math.abs(Number(transaction.amount)).toFixed(2)}. This account's balance and reconciliation still use the whole amount. A Transfer part sent to another account also shows up in that account.
        </p>

        {others.length > 0 && (
          <div className="mb-3">
            {!loan ? (
              <button type="button" onClick={() => setLoan({ account: '', principal: '', interest: '' })} className="text-blue-600 text-sm">Loan payment (principal + interest)</button>
            ) : (
              <div className="bg-gray-50 border rounded p-3 grid grid-cols-12 gap-2 items-center" data-testid="loan-helper">
                <select aria-label="Loan account" value={loan.account} onChange={(e) => setLoan({ ...loan, account: e.target.value })} className="col-span-4 border rounded px-2 py-1">
                  <option value="">Loan account</option>
                  {others.map((a) => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                </select>
                <input aria-label="Principal" type="number" step="0.01" min="0" placeholder="Principal" value={loan.principal} onChange={(e) => setLoan({ ...loan, principal: e.target.value })} className="col-span-2 border rounded px-2 py-1" />
                <input aria-label="Interest" type="number" step="0.01" min="0" placeholder="Interest" value={loan.interest} onChange={(e) => setLoan({ ...loan, interest: e.target.value })} className="col-span-2 border rounded px-2 py-1" />
                <button type="button" onClick={applyLoan} disabled={!loanParts} title={loanParts ? '' : 'Principal plus interest must equal the payment'} className="col-span-2 px-2 py-1 bg-blue-600 text-white rounded disabled:opacity-50">Fill parts</button>
                <button type="button" onClick={() => setLoan(null)} className="col-span-2 px-2 py-1 border rounded">Cancel</button>
                {!interestCategory && <p className="col-span-12 text-xs text-gray-600">Pick the interest category on the second part.</p>}
              </div>
            )}
          </div>
        )}

        {parts.map((p, i) => (
          <div key={i} className="mb-2">
            <div className="grid grid-cols-12 gap-2">
              <select aria-label="Part type" value={p.transaction_type} onChange={(e) => update(i, 'transaction_type', e.target.value)} className="col-span-2 border rounded px-2 py-1">
                {PART_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <select aria-label="Part category" value={p.category_id} onChange={(e) => update(i, 'category_id', e.target.value)} className="col-span-4 border rounded px-2 py-1">
                <option value="">{p.transaction_type === 'transfer' ? 'No category' : 'Select category'}</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input aria-label="Part amount" type="number" step="0.01" min="0" value={p.amount} onChange={(e) => update(i, 'amount', e.target.value)} className="col-span-2 border rounded px-2 py-1" />
              <input aria-label="Part memo" type="text" placeholder="Memo" value={p.memo} onChange={(e) => update(i, 'memo', e.target.value)} className="col-span-3 border rounded px-2 py-1" />
              <button type="button" onClick={() => setParts(parts.filter((_, j) => j !== i))} disabled={parts.length <= 2} className="col-span-1 text-red-600 disabled:text-gray-300">{'\u2715'}</button>
            </div>
            {p.transaction_type === 'transfer' && others.length > 0 && (
              <div className="grid grid-cols-12 gap-2 mt-1">
                <select aria-label="Other account" value={p.counter_account_id || ''} onChange={(e) => update(i, 'counter_account_id', e.target.value)} className="col-start-3 col-span-6 border rounded px-2 py-1 text-sm">
                  <option value="">Other account: not set</option>
                  {others.map((a) => <option key={a.id} value={a.id}>{a.account_name}</option>)}
                </select>
                {p.counter_status && p.counter_account_name && (
                  <span className="col-span-4 text-xs text-gray-600 self-center">{p.counter_account_name}: {STATUS_TEXT[p.counter_status]}</span>
                )}
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={() => setParts([...parts, blank('expense')])} className="text-blue-600 text-sm mb-3">+ Add part</button>
        <p className={`text-sm mb-2 ${left === 0 ? 'text-green-700' : 'text-red-600'}`}>
          {left === 0 ? 'Parts add up.' : `${left > 0 ? 'Left to assign' : 'Over by'}: ${(Math.abs(left) / 100).toFixed(2)}`}
        </p>
        <p className="text-xs text-gray-600 mb-2">For a Transfer part with an other account, a matching row there is linked; otherwise one is recorded there. Bank-connected accounts never get a made-up row.</p>
        {error && <p className="text-sm text-red-600 mb-2" role="alert">{error}</p>}
        <div className="flex justify-between mt-4">
          <button type="button" onClick={remove} disabled={saving} className="px-4 py-2 border rounded text-red-600">Remove split</button>
          <div className="space-x-3">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded">Cancel</button>
            <button type="button" onClick={save} disabled={saving || !!problem} title={problem || ''} className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50">Save split</button>
          </div>
        </div>
      </div>
    </div>
  );
}
